import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useUserCiphers } from '../hooks/useUserCiphers'
import { encode, decode, buildCodebook, getCharacterCipherDescription } from '../lib/cipher'
import './CipherToolPage.css'

export default function CipherToolPage() {
  const { ciphers, loading } = useUserCiphers()

  const [selectedCipherId, setSelectedCipherId] = useState('')
  const [mode, setMode] = useState('decode')
  const [inputText, setInputText] = useState('')
  const [outputText, setOutputText] = useState('')
  const [showCodebook, setShowCodebook] = useState(false)

  const selectedCipher = ciphers.find(c => c.id === selectedCipherId)

  const handleProcess = () => {
    if (!selectedCipher || !inputText.trim()) {
      setOutputText('')
      return
    }

    try {
      const result = mode === 'encode'
        ? encode(inputText, selectedCipher)
        : decode(inputText, selectedCipher)
      setOutputText(result)
    } catch (err) {
      setOutputText(`Error: ${err.message}`)
    }
  }

  const handleCipherChange = (cipherId) => {
    setSelectedCipherId(cipherId)
    setOutputText('')
  }

  const handleSwap = () => {
    setInputText(outputText)
    setOutputText('')
    setMode(mode === 'encode' ? 'decode' : 'encode')
  }

  if (loading) {
    return (
      <div className="cipher-tool-page">
        <div className="container">
          <div className="loading-spinner" />
        </div>
      </div>
    )
  }

  const codebook = selectedCipher ? buildCodebook(selectedCipher.substitutions) : []
  const charCipherDesc = selectedCipher ? getCharacterCipherDescription(selectedCipher) : null

  return (
    <div className="cipher-tool-page">
      <div className="container">
        <header className="cipher-header">
          <div className="cipher-title-row">
            <Link to="/tools" className="back-link">← Tools</Link>
            <h1>Cipher Tool</h1>
          </div>
          <p className="cipher-subtitle">Encode and decode secret messages</p>
        </header>

        {ciphers.length === 0 ? (
          <div className="cipher-empty">
            <h2>No Ciphers Available</h2>
            <p>You don't have access to any ciphers yet.</p>
            <p className="text-secondary">
              Characters must learn ciphers before their players can use them.
            </p>
          </div>
        ) : (
          <div className="cipher-workspace">
            <div className="cipher-controls">
              <div className="control-group">
                <label htmlFor="cipher-select">Cipher</label>
                <select
                  id="cipher-select"
                  value={selectedCipherId}
                  onChange={(e) => handleCipherChange(e.target.value)}
                >
                  <option value="">Select a cipher...</option>
                  {ciphers.map(cipher => (
                    <option key={cipher.id} value={cipher.id}>
                      {cipher.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="control-group mode-toggle">
                <label>Mode</label>
                <div className="toggle-buttons">
                  <button
                    type="button"
                    className={`toggle-btn ${mode === 'decode' ? 'active' : ''}`}
                    onClick={() => setMode('decode')}
                  >
                    Decode
                  </button>
                  <button
                    type="button"
                    className={`toggle-btn ${mode === 'encode' ? 'active' : ''}`}
                    onClick={() => setMode('encode')}
                  >
                    Encode
                  </button>
                </div>
              </div>
            </div>

            {selectedCipher && (
              <p className="cipher-description">{selectedCipher.description}</p>
            )}

            <div className="cipher-io">
              <div className="io-section">
                <label htmlFor="cipher-input">
                  {mode === 'encode' ? 'Plain Text' : 'Cipher Text'}
                </label>
                <textarea
                  id="cipher-input"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder={mode === 'encode'
                    ? 'Enter plain text to encode...'
                    : 'Enter cipher text to decode...'
                  }
                  rows={6}
                />
              </div>

              <div className="io-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleProcess}
                  disabled={!selectedCipher || !inputText.trim()}
                >
                  {mode === 'encode' ? 'Encode →' : 'Decode →'}
                </button>
                {outputText && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={handleSwap}
                    title="Swap input/output and reverse mode"
                  >
                    ⇄ Swap
                  </button>
                )}
              </div>

              <div className="io-section">
                <label htmlFor="cipher-output">
                  {mode === 'encode' ? 'Cipher Text' : 'Plain Text'}
                </label>
                <textarea
                  id="cipher-output"
                  value={outputText}
                  readOnly
                  placeholder="Output will appear here..."
                  rows={6}
                />
                {outputText && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm copy-btn"
                    onClick={() => navigator.clipboard.writeText(outputText)}
                  >
                    Copy
                  </button>
                )}
              </div>
            </div>

            {selectedCipher && (
              <div className="codebook-section">
                <button
                  type="button"
                  className="codebook-toggle"
                  onClick={() => setShowCodebook(!showCodebook)}
                >
                  {showCodebook ? '▼' : '▶'} Codebook Reference
                  <span className="codebook-count">({codebook.length} entries)</span>
                </button>

                {showCodebook && (
                  <div className="codebook">
                    {charCipherDesc && (
                      <div className="char-cipher-info">
                        <strong>Character Cipher:</strong> {charCipherDesc}
                      </div>
                    )}

                    {codebook.length > 0 && (
                      <table className="codebook-table">
                        <thead>
                          <tr>
                            <th>Plain</th>
                            <th>Cipher</th>
                          </tr>
                        </thead>
                        <tbody>
                          {codebook.map((entry, i) => (
                            <tr key={i} className={entry.isCanonical ? 'canonical' : ''}>
                              <td>
                                {entry.plain}
                                {entry.isCanonical && <span className="canonical-badge">★</span>}
                              </td>
                              <td>{entry.cipher}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {codebook.length === 0 && charCipherDesc && (
                      <p className="codebook-note">
                        This cipher uses only character-level encoding (no phrase substitutions).
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
